import {API_DOMAIN} from "../constants";
import axios from "axios";

export async function authenticate(payload) {
    // Step 1: obtain a token from AuthService via the v3 login pass-through. This endpoint returns
    // only { accessToken, refreshToken, expiresAt } in the body (no Authorization header, no account
    // data), so the account is fetched separately below.
    const loginResponse = await axios({
        url: API_DOMAIN + "/api/v3/auth/login",
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
        },
        data: {
            login: payload.login,
            password: payload.password,
        },
    });

    const authToken = "Bearer " + loginResponse.data.accessToken;

    // Step 2: fetch the current user's account (adminPortalAccess, repType, authorities, ...) using
    // the freshly minted token. This is the same account payload the old details=true login returned.
    const userData = await getAccount(authToken);

    return {
        userData,
        authToken,
    };
}

// GET /api/v2/account — the current caller's account payload (adminPortalAccess, repType,
// authorities, ...). Used both by the sign-in flow (with the freshly minted token) and by the
// per-page-load refresh (with the stored token).
export async function getAccount(token) {
    const response = await axios({
        url: API_DOMAIN + "/api/v2/account",
        method: "GET",
        headers: {
            Accept: "application/json",
            Authorization: token || localStorage.getItem("MFE_TOKEN"),
        },
    });

    return response.data;
}

// Validate local password reset token (unauthenticated)
// GET /api/v3/auth/localPasswordResetTokens/{token}
// Returns an object with at least an `email` field when valid
export async function getLocalPasswordResetToken(token) {
    const response = await axios({
        url: API_DOMAIN + "/api/v3/auth/localPasswordResetTokens/" + encodeURIComponent(token),
        method: "GET",
        headers: {
            Accept: "application/json",
        },
    });

    return response.data;
}

// Reset local password (unauthenticated)
// POST /api/v3/auth/resetLocalPassword
// Payload: { newPassword: string, token: string }
export async function resetLocalPassword({newPassword, token}) {
    const response = await axios({
        url: API_DOMAIN + "/api/v3/auth/resetLocalPassword",
        method: "POST",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
        },
        data: {
            newPassword,
            token,
        },
    });

    return response.data;
}

export async function getAuthorities() {
    return axios({
        url: API_DOMAIN + "/api/v2/authorities",
        method: "GET",
        headers: {
            Accept: "application/json",
            Authorization: localStorage.getItem("MFE_TOKEN"),
        },
    });
}

export async function getManagedAuthorities(userId) {
    const response = await axios({
        url: API_DOMAIN + "/api/v2/repTypes",
        method: "GET",
        headers: {
            Accept: "application/json",
            Authorization: localStorage.getItem("MFE_TOKEN"),
        },
        params: {
            managedByUserId: userId,
        },
    });

    return response.data;
}

export async function getRepPermissions(businessUnitCode, repTypeId) {
    const response = await axios({
        url: API_DOMAIN + "/api/v2/repPermissions",
        method: "GET",
        headers: {
            Accept: "application/json",
            Authorization: localStorage.getItem("MFE_TOKEN"),
        },
        params: {
            businessUnitCode: businessUnitCode,
            repTypeId: repTypeId,
        },
    });

    return response.data;
}
export async function sendLocalPasswordResetEmail({email}) {
    const response = await axios({
        url: API_DOMAIN + "/api/v2/sendLocalPasswordResetEmail",
        method: "POST",
        headers: {
            Accept: "application/json",
            Authorization: localStorage.getItem("MFE_TOKEN"),
        },
        params: {
            email,
            platform: "ADMIN_PORTAL",
        },
    });

    return response.data;
}
export async function sendLocalPasswordResetEmailAdminPortal({email, login}) {
    const response = await axios({
        url: API_DOMAIN + "/api/v2/sendLocalPasswordResetEmail/adminPortal",
        method: "POST",
        headers: {
            Accept: "application/json",
            Authorization: localStorage.getItem("MFE_TOKEN"),
        },
        params: {
            email,
            login,
            platform: "ADMIN_PORTAL",
        },
    });

    return response.data;
}
export async function resetLocalPasswordAdminPortal({id, password}) {
    const response = await axios({
        url: API_DOMAIN + "/api/v2/resetLocalPassword/adminPortal",
        method: "POST",
        headers: {
            Accept: "application/json",
            Authorization: localStorage.getItem("MFE_TOKEN"),
        },
        data: {
            id,
            password,
        },
    });

    return response.data;
}

// GET /api/v3/auth/me/permissions — the flat list of permission-claim values the authenticated caller
// holds, unioned across the individual, group, user-type and business-unit grant paths (TMMFX-3571).
export async function getMyPermissions() {
    const response = await axios({
        url: API_DOMAIN + "/api/v3/auth/me/permissions",
        method: "GET",
        headers: {
            Accept: "application/json",
            Authorization: localStorage.getItem("MFE_TOKEN"),
        },
    });

    return response.data?.permissions ?? [];
}
